<script setup lang="ts">
import { CirclePlus, Fold, Search } from "@element-plus/icons-vue";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { Entity, EntityDefinitionDto } from "~/api";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { useOrgInfoStore } from "~/stores/OrgInfoStore";
import { apiClient } from "~/utils/apiClient";
import EntityInstanceCreator from "../components/EntityInstanceCreator.vue";

const route = useRoute();
const router = useRouter();
const myInfoStore = useMyInfoStore();
const orgInfoStore = useOrgInfoStore();

const entities = ref<Entity[]>([]);
const loading = ref(true);
const searchQuery = ref<string>("");

// Get the entity definition GUID from the route meta
const entityDefinitionGuid = computed<string>(
  () => (route.meta.entityDefinitionGuid as string) ?? "",
);

const currentEntityDefinitionDto = ref<EntityDefinitionDto | null>(null);

const entityDefinitionName = computed(
  () => currentEntityDefinitionDto.value?.name ?? "Entity",
);
const entityDefinitionNamePlural = computed(
  () => currentEntityDefinitionDto.value?.plural ?? "Entities",
);

const filteredEntities = computed(() => {
  const searchQueryLowered = searchQuery.value.toLowerCase();
  const searchFiltered =
    searchQueryLowered !== "" && searchQueryLowered !== undefined;

  return entities.value.filter((entity: Entity) => {
    if (searchFiltered) {
      const name = entity.name?.toLowerCase().includes(searchQueryLowered);
      if (!name) {
        return false;
      }
    }
    return true;
  });
});

let retrieving = false;
const retrieveEntities = async () => {
  if (retrieving || !entityDefinitionGuid.value) {
    return;
  }

  retrieving = true;
  try {
    entities.value = await apiClient.entity.getEntities(
      myInfoStore.currentSysOrgGuid,
      entityDefinitionGuid.value,
    );
  } finally {
    retrieving = false;
  }
};

const entityDialogOpen = ref(false);
const entityOpenCounter = ref(0);

const createEntity = () => {
  entityOpenCounter.value++;
  entityDialogOpen.value = true;
};

const handleAddEntity = (entity: Entity) => {
  entities.value.push(entity);
  // Navigate to the newly created entity's CRM view
  navigateToEntity(entity);
};

const navigateToEntity = (entity: Entity) => {
  const name = router.currentRoute.value.path.startsWith("/portal")
    ? "PortalCRMEntityDetail"
    : "CRMEntityDetail";
  router.push({
    name: name,
    params: {
      entityDefinitionGuid: entityDefinitionGuid.value,
      entityGuid: entity.guid,
    },
  });
};

const loadData = async () => {
  loading.value = true;
  await myInfoStore.load();
  await retrieveEntities();
  loading.value = false;
};

const findEntityDefinition = (
  entities: EntityDefinitionDto[],
  guid: string,
): EntityDefinitionDto | null => {
  for (const entity of entities) {
    if (entity.guid === guid) {
      return entity;
    }
    if (entity.childEntityDefinitions?.length) {
      const found = findEntityDefinition(entity.childEntityDefinitions, guid);
      if (found) return found;
    }
  }
  return null;
};

// Watch for route changes to reload data when navigating between CRM entity types
watch(
  () => route.meta.entityDefinitionGuid,
  async (newGuid) => {
    if (newGuid) {
      currentEntityDefinitionDto.value = findEntityDefinition(
        orgInfoStore.MyOrgInfo?.entityDefinitionDtos ?? [],
        newGuid as string,
      );
      await loadData();
    }
  },
);

onMounted(async () => {
  await orgInfoStore.load();
  if (entityDefinitionGuid.value) {
    currentEntityDefinitionDto.value = findEntityDefinition(
      orgInfoStore.MyOrgInfo?.entityDefinitionDtos ?? [],
      entityDefinitionGuid.value,
    );
    if (currentEntityDefinitionDto.value) {
      await loadData();
    }
  }
});
</script>

<template>
  <div class="CRMEntityList">
    <div class="list-header">
      <div class="header-title">
        <i :class="`mdi mdi-${currentEntityDefinitionDto?.icon}`" />
        <h2>{{ entityDefinitionNamePlural }}</h2>

        <el-tag v-if="filteredEntities.length > 0" size="small" type="info">
          {{ filteredEntities.length }}
        </el-tag>
      </div>
      <div v-if="!loading" class="header-actions">
        <el-input
          v-model="searchQuery"
          class="search"
          clearable
          placeholder="Search..."
          :prefix-icon="Search"
          style="width: 250px"
        />

        <el-button :icon="CirclePlus" type="primary" @click="createEntity">
          New {{ entityDefinitionName }}
        </el-button>
      </div>
    </div>

    <el-table
      v-loading="loading"
      :data="filteredEntities"
      class="entity-table"
      :default-sort="{ prop: 'name', order: 'ascending' }"
      @row-click="navigateToEntity"
    >
      <el-table-column
        prop="name"
        :label="entityDefinitionName"
        sortable
        width="300"
      >
        <template #default="scope">
          <div class="entity-name-cell">
            <i :class="`mdi mdi-${currentEntityDefinitionDto?.icon}`" />

            <el-link type="primary" @click.stop="navigateToEntity(scope.row)">
              {{ scope.row.name }}
            </el-link>
          </div>
        </template>
      </el-table-column>

      <el-table-column prop="status" label="Status" width="120" sortable>
        <template #default="scope">
          <el-tag v-if="scope.row.status" size="small" type="info">
            {{ scope.row.status }}
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column label="Actions">
        <template #default="scope">
          <el-button
            :icon="Fold"
            size="small"
            type="primary"
            text
            title="View CRM Details"
            @click.stop="navigateToEntity(scope.row)"
          >
            View
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- Create Entity Dialog -->
    <EntityInstanceCreator
      v-if="entityDefinitionGuid"
      :key="entityOpenCounter"
      v-model="entityDialogOpen"
      :entity-definition-guid="entityDefinitionGuid"
      @add:entity="handleAddEntity"
    />
  </div>
</template>

<style lang="less" scoped>
.CRMEntityList {
  padding: 1.5rem;
  height: 100%;
  display: flex;
  flex-direction: column;

  .list-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    flex-wrap: wrap;
    gap: 1rem;

    .header-title {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 1.5rem;

      h2 {
        margin: 0;
        font-size: 1.5rem;
        font-weight: 600;
      }
    }

    .header-actions {
      display: flex;
      align-items: center;
      gap: 1rem;

      .showing-count {
        color: var(--el-text-color-secondary);
        font-size: 0.9rem;
      }
    }
  }

  .entity-table {
    flex: 1;

    .entity-name-cell {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
  }

  :deep(.el-table__row) {
    cursor: pointer;

    &:hover {
      background-color: var(--el-fill-color-light);
    }
  }
}
</style>
